
import React from 'react';

const GameEvolution = () => {
  const evolutionPairs = [
    { 
      base: "Pikachu", 
      evolved: "Raichu",
      requirements: "50,000 clicks + Thunder Stone upgrade",
      benefits: "3x click power, electric-type bonus +25%"
    },
    { 
      base: "Eevee", 
      evolved: "Multiple forms",
      requirements: "Evolution stones + specific conditions",
      benefits: "Different bonuses based on evolution form"
    },
    { 
      base: "Magikarp", 
      evolved: "Gyarados",
      requirements: "100,000 clicks (highest requirement)",
      benefits: "10x click power, water-type bonus +50%"
    },
    { 
      base: "Growlithe", 
      evolved: "Arcanine",
      requirements: "25,000 clicks + Fire Stone upgrade",
      benefits: "2.5x click power, fire-type bonus +30%"
    },
    { 
      base: "Abra", 
      evolved: "Kadabra → Alakazam",
      requirements: "Two-stage evolution, trading mechanic",
      benefits: "Progressive bonuses to auto-click rate"
    },
    { 
      base: "Machop", 
      evolved: "Machoke → Machamp",
      requirements: "Progressive strength challenges",
      benefits: "Multipliers to click power and critical rate"
    }
  ];

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-lg shadow-xl overflow-hidden">
        <div className="p-6 sm:p-10">
          <h2 className="text-3xl font-bold text-center text-white mb-8">
            Evolution System in Pokemon Clicker 2
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
              One of the most exciting features in Pokemon Clicker 2 is the evolution system, which mirrors the main Pokemon games. As you click and accumulate resources, your Pokemon can evolve into more powerful forms, dramatically increasing your clicking power and unlocking new abilities.
            </p>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">How Evolution Works</h3>
            
            <div className="space-y-6 mb-8">
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Click Requirements</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Most Pokemon require a specific number of clicks before they become eligible for evolution. This represents the experience they gain through your interaction.
                </p>
              </div>
              
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Evolution Items</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Some Pokemon need special items like Thunder Stones, Fire Stones, or Water Stones to evolve. These can be purchased in the shop with PokeDollars or found during special events.
                </p>
              </div>
              
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Friendship Evolution</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Certain Pokemon evolve based on friendship levels, which increase as you actively click on them rather than relying on auto-clickers.
                </p>
              </div>
              
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Trade Evolution</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Pokemon Clicker 2 simulates trade evolutions through a special mechanic where you can "trade" resources for instant evolution of eligible Pokemon.
                </p>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Notable Evolution Chains</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {evolutionPairs.map((pair, index) => (
                <div key={index} className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 p-4 rounded-lg shadow">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-bold text-gray-900 dark:text-white">{pair.base}</span>
                    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className="font-bold text-gray-900 dark:text-white">{pair.evolved}</span>
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-700 dark:text-gray-300"><strong>Requirements:</strong> {pair.requirements}</p>
                    <p className="text-gray-700 dark:text-gray-300"><strong>Benefits:</strong> {pair.benefits}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Evolution Strategy Tips</h3>
            
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Prioritize high-value evolutions</strong> - Focus first on Pokemon that give the largest bonuses when evolved</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Save for evolution items</strong> - Don't spend all your currency on minor upgrades if you need stones for important evolutions</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Balance your evolution types</strong> - Having evolved Pokemon of different types provides varied bonuses</span>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Special evolutionary forms matter</strong> - Some evolutions (like Eevee's) offer unique strategic advantages</span>
              </li>
            </ul>
            
            <div className="mt-8 p-4 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <p className="text-gray-800 dark:text-yellow-100 font-medium">
                <strong>Pro Tip:</strong> The Magikarp to Gyarados evolution has the highest click requirement but offers the most substantial power increase in the entire game—a whopping 10x boost to your clicking power! Consider saving up for this evolution if you're planning for the long term.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameEvolution;
